import * as Net from 'util/Net';

export interface MeteringSettings {
  isUserCreditEnforcementEnabled: boolean;
  monthlyCreditLimit: number;
  tenantCreditLimitId?: string | null;
  enableOverages: boolean;
  overagesLimit: number;
}

/**
 * Read the tenant's current metering settings. The per-user credit limit is stored per tenant rather
 * than exposed to the client at bootstrap, so it has to be fetched to be shown or edited.
 */
export function fetchMeteringSettings(): Promise<MeteringSettings> {
  return Net.getJSONPromise('/1/api/metering/settings');
}

export interface MeteringSettingsUpdate {
  isUserCreditEnforcementEnabledUpdate: boolean | null;
  meteringTenantPerUserCreditLimitUpdate?: number | null;
}

export function updateMeteringSettings(meteringSettingsUpdate: MeteringSettingsUpdate): Promise<void> {
  return Net.postJSONPromise({
    url: '/1/api/metering/settings',
    data: meteringSettingsUpdate,
  });
}

interface BaseGroupLimit {
  id?: string;
  monthlyLimit: number;
}

export interface TenantGroupLimit extends BaseGroupLimit {
  groupType: 'TENANT';
}

export interface IncreasedGroupLimit extends BaseGroupLimit {
  groupType: 'TEAM' | 'ORGANIZATION';
  memberCount: number;
  groupName: string;
  organizationName?: string; // Only set if Group is a team
  groupApiId: string;
}

export type GroupLimit = TenantGroupLimit | IncreasedGroupLimit;

export function isIncreasedGroupLimit(limit: GroupLimit): limit is IncreasedGroupLimit {
  return limit.groupType !== 'TENANT';
}

interface MutatedGroupLimitInput {
  id?: string;
  groupType: GroupLimit['groupType'];
  monthlyLimit: number;
  groupApiId?: string;
  isPendingDelete?: boolean;
}

/**
 * A single credit limit change. `isDelete` marks a team/org entry for deletion and carries the row's
 * preserved `monthlyLimit` (not a zeroed one); `creditLimitId` is undefined for an add and set for an
 * update or delete.
 */
export interface CreditLimitInput {
  creditLimitId?: GroupLimit['id'];
  groupType: 'TENANT' | 'TEAM' | 'ORGANIZATION';
  monthlyLimit: number;
  isDelete?: boolean;
  teamId?: string;
  organizationId?: string;
}

export function groupLimitToCreditLimitInput(limit: MutatedGroupLimitInput): CreditLimitInput {
  return {
    creditLimitId: limit.id,
    groupType: limit.groupType,
    monthlyLimit: limit.monthlyLimit,
    teamId: limit.groupType === 'TEAM' ? limit.groupApiId : undefined,
    organizationId: limit.groupType === 'ORGANIZATION' ? limit.groupApiId : undefined,
    isDelete: limit.isPendingDelete ?? false,
  };
}

export async function fetchGroupCreditLimits(): Promise<Array<IncreasedGroupLimit>> {
  return (await Net.getJSONPromise('/1/api/metering/group-limits')).groupLimits.filter((limit: GroupLimit) =>
    isIncreasedGroupLimit(limit)
  );
}

export function mutateGroupCreditLimits(
  creditLimits: Array<MutatedGroupLimitInput>
): Promise<{groupLimits: Array<GroupLimit>}> {
  return Net.postJSONPromise({
    url: '/1/api/metering/mutate-credit-limits',
    data: {creditLimits: creditLimits.map((limit) => groupLimitToCreditLimitInput(limit))},
  });
}

export interface MeteredUser {
  userApiId: string;
  userName: string;
  creditsUsed: number;
}

export interface UsersBlockedByLimitChangeResponse {
  blockedUsers: Array<MeteredUser>;
}

/**
 * Preview which users have already used at or above their effective limit under the proposed changes
 * this billing cycle, and would be blocked from using any metered AI features if these
 * credit limit changes were applied. A user's effective limit is the highest limit across their
 * tenant, team, and org groups. Used to warn admins before they save.
 */
export function fetchUsersBlockedByLimitChange(
  creditLimits: Array<MutatedGroupLimitInput>
): Promise<UsersBlockedByLimitChangeResponse> {
  return Net.postJSONPromise({
    url: '/1/api/metering/users-blocked-by-limit-change',
    data: {creditLimits: creditLimits.map((limit) => groupLimitToCreditLimitInput(limit))},
  });
}

export interface UserUsageSummaryResponse {
  usage: number;
  // Null when per-user credit limits are not enforced for the tenant.
  effectiveLimit: number | null;
  // UTC ISO date on which the current billing period started (e.g. "2026-09-01")
  periodStart: string;
  // UTC ISO string (e.g. "2026-10-01")
  resetDate: string;
  isTenantOutOfCredits: boolean;
}

/** Read the current user's AI usage summary. */
export function fetchUsageSummaryForUser(): Promise<UserUsageSummaryResponse> {
  return Net.getJSONPromise('/1/api/metering/usage-summary-for-user');
}
